import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { INSIGHTS_BACKEND_URL } from "@/lib/axios";
import { useScenarioBuilderClient } from "@/pages/scenario-builder/api/hooks/useScenarioBuilderClient";
import { axiosApi } from "@/Utils";

import { createCommentV2 } from "../v2/createComments";

interface CreateCommentParams {
  scenarioId: string;
  commentData: string;
}

export const createComment = async ({
  scenarioId,
  commentData,
}: CreateCommentParams): Promise<void> => {
  await axiosApi.post(
    `/api/v2/scenarios-new/${scenarioId}/comment`,
    { comment: commentData },
    {
      baseURL: INSIGHTS_BACKEND_URL,
    }
  );
};

interface UseCreateCommentOptions {
  scenarioId?: string;
  isScenariosApiEnabled?: boolean;
}

export const useCreateComment = ({
  scenarioId,
  isScenariosApiEnabled = false,
}: UseCreateCommentOptions = {}) => {
  const queryClient = useQueryClient();
  const scenarioBuilderClient = useScenarioBuilderClient();

  const createCommentWrapper = async (params: { scenarioId: string; commentData: string }) => {
    if (isScenariosApiEnabled) {
      return createCommentV2({
        scenarioBuilderClient,
        scenarioId: params.scenarioId,
        comment: params.commentData,
      });
    } else {
      return createComment(params);
    }
  };

  return useMutation({
    mutationFn: createCommentWrapper,
    onSuccess: () => {
      toast.success("Comment added successfully");
      if (!scenarioId) return;
      if (isScenariosApiEnabled) {
        queryClient.invalidateQueries({ queryKey: ["activity-log-v2", scenarioId] });
      } else {
        queryClient.invalidateQueries({ queryKey: ["activity-logs", scenarioId] });
      }
    },
    onError: () => {
      toast.error("Failed to add comment. Please try again.");
    },
  });
};
